import styled from 'styled-components';

// flex: 1 + min-height: 0 + overflow-y: auto = the panel becomes the single
// scroll context for the inspector. Comments and Edits stack naturally; if
// the total exceeds the viewport, this container scrolls.
export const Container = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow-y: auto;

	/* Align the collapsible-section headers (chevron + label) with the element-
	   name area above. CollapsibleSection's Header is a button rendered as a
	   direct child of this Container; padding it left here gives the chevron
	   the same gutter that HeaderBase has, without modifying the shared widget.
	   Margin-top opens space above each section header (separating it from
	   prior content); margin-bottom: 0 overrides the widget's built-in 8px so
	   the header sits tight against its own content (visually groups with it). */
	& > button {
		padding-left: 12px;
		margin-top: 8px;
		margin-bottom: 0;
	}
`;

export const HeaderBase = styled.div`
	padding-left: 12px;
	padding-right: 10px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	background: transparent;
	padding-top: 18px;
	padding-bottom: 12px;
`;

// Wrapper for the title block (ElementName + optional DsComponentName).
// flex: 1 + min-width: 0 lets the inner ElementName's ellipsis kick in —
// without min-width: 0 the flex item refuses to shrink past its content width
// and pushes the trash button off the edge.
export const TitleBlock = styled.div`
	flex: 1;
	min-width: 0;
`;

export const ElementName = styled.div`
	font-size: 16px;
	font-family: 'Inter', sans-serif;
	font-weight: 600;
	color: ${({ theme }) => theme.gray[1000]};
	flex: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const DsComponentName = styled.div`
	font-size: 13px;
	font-family: 'JetBrains Mono', monospace;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[1000]};
	margin-top: 8px;
`;

// Trash button in HeaderBase top-right. Quiet by default; reddens on hover so
// the destructive intent reads without screaming on every selection change.
export const DeleteButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	padding: 0;
	background: transparent;
	border: none;
	border-radius: 4px;
	color: ${({ theme }) => theme.gray[600]};

	&:hover {
		background: ${({ theme }) => theme.gray[100]};
		color: ${({ theme }) => theme.gray[1000]};
	}

	&:disabled {
		opacity: 0.4;
		pointer-events: none;
	}
`;

// Wrapper for Comments inside the CollapsibleSection body. No height
// constraint — Comments sizes to its content and the outer Container's
// overflow handles scrolling for the whole panel.
export const CommentsWrapper = styled.div`
	display: flex;
`;

// Transparent overlay that catches hovers for the disabled tooltip while
// frame chat is processing.
export const DisabledOverlay = styled.div`
	position: absolute;
	inset: 0;
	z-index: 1;
	pointer-events: auto;
`;

// Wraps Text + Style sections. Dims and blocks interaction while processing.
export const EditToolsWrapper = styled.div<{ $disabled: boolean }>`
	position: relative;
	${({ $disabled }) =>
		$disabled &&
		`& > *:not(${DisabledOverlay}) {
			opacity: 0.4;
			pointer-events: none;
		}`}
`;

// Wrapper used inside any of the right panel's collapsible sections (Text,
// Style). Tight padding-top keeps the section grouped with its own header.
export const SectionWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 8px 12px 8px 12px;
`;
